import React, { useEffect, useState } from "react";
import { userService } from "../services/userService";
import { Link, useLocation, useNavigate } from "react-router-dom";
import PhoneInput, { parsePhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import {
  LuChevronDown,
  LuMail,
  LuUserRound,
  LuUsersRound,
} from "react-icons/lu";
import companyController from "../controllers/companyController";
import roleController from "../controllers/roleController";
import Utils from "../utils/Utils";
import VendorLayout from "./VendorLayout";
import ActiveIcon from "../icons/ActiveIcon";
import UserAddedSuccessIcon from "../icons/UserAddedSuccessIcon";
import { getStoredUserId } from "./vendorSession";
import "./vendorUserManagement.css";
import { translateLabel, useTranslationSnapshot } from "./../translations/runtime";

const addUserFeedbackLabels = new Map([
  ["Company id was not found for this user.", "txt_web_vendor_company_id_was_not_found_for_this_user"],
  ["Unable to load roles.", "txt_web_vendor_unable_to_load_roles"],
  ["First name and last name can only contain letters.", "txt_web_vendor_first_name_and_last_name_can_only_contain_letters"],
  ["Admin id was not found for this user.", "txt_web_vendor_admin_id_was_not_found_for_this_user"],
  ["Phone number is required.", "txt_web_vendor_phone_number_is_required"],
  ["Roles are still loading.", "txt_web_vendor_roles_are_still_loading"],
  ["Role id must be a valid UUID.", "txt_web_vendor_role_id_must_be_a_valid_uuid"],
  ["Unable to create user.", "txt_web_vendor_unable_to_create_user"],
]);
const translateAddUserFeedback = (message) => {
  const id = addUserFeedbackLabels.get(message);
  return id ? translateLabel(id, message) : message;
};

const unwrapPayload = (response) =>
  response?.data?.data ?? response?.data?.result ?? response?.data ?? {};

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const toApiBirthdate = (value) => {
  if (!value) return "";

  const [year, month, day] = value.split("-");
  if (!year || !month || !day) return value;

  return `${day}/${month}/${year}`;
};

const getPhonePayload = (phoneNumber) => {
  const normalized = String(phoneNumber || "").replace(/\s+/g, "");
  const parsedPhone = normalized ? parsePhoneNumber(normalized) : null;

  if (parsedPhone?.countryCallingCode && parsedPhone?.nationalNumber) {
    return {
      phoneNumber: parsedPhone.nationalNumber,
      suffix: parsedPhone.countryCallingCode,
    };
  }

  const withoutPlus = normalized.replace(/^\+/, "");

  if (withoutPlus.startsWith("961")) {
    return {
      phoneNumber: withoutPlus.slice(3),
      suffix: "961",
    };
  }

  return {
    phoneNumber: withoutPlus,
    suffix: "961",
  };
};

const isUuid = (value) => UUID_PATTERN.test(String(value || ""));

const getGenderPronoun = (genderName) => {
  if (genderName === "MALE") return "HIS";
  if (genderName === "FEMALE") return "HER";

  return null;
};

function VendorAddUser() {
  useTranslationSnapshot();
  const location = useLocation();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    birthdate: "",
    email: "",
    firstName: "",
    gender: "",
    lastName: "",
    roleID: "",
  });
  const [phoneNumber, setPhoneNumber] = useState();
  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [rolesError, setRolesError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [createdUserCreatedAt, setCreatedUserCreatedAt] = useState("");
  const isPreview = location.pathname.endsWith("-preview");
  const userManagementPath = isPreview
    ? "/vendor-user-management-preview"
    : "/vendor-user-management";

  const updateField = (field) => (event) => {
    const value =
      field === "firstName" || field === "lastName"
        ? Utils.sanitizeNameInput(event.target.value)
        : event.target.value;

    setForm((currentForm) => ({
      ...currentForm,
      [field]: value,
    }));
  };

  useEffect(() => {
    let isCurrentRequest = true;

    const fetchRoles = async () => {
      setRolesLoading(true);
      setRolesError("");

      try {
        const Authorization = userService.getJwt();
        const companyId = userService.getUserData().Compid;

        if (!companyId) {
          throw new Error("Company id was not found for this user.");
        }

        const response = await roleController.getAllRoles({
          Authorization,
          application: 3,
          companyId,
        });

        if (response?.isAxiosError || response?.response) {
          throw response;
        }

        const payload = unwrapPayload(response);
        const roleList = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.content)
            ? payload.content
            : [];

        if (!isCurrentRequest) return;

        setRoles(
          roleList.filter((role) => role?.roleID && role?.roleName),
        );
      } catch (requestError) {
        if (!isCurrentRequest) return;

        setRoles([]);
        setRolesError(
          requestError?.response?.data?.message ||
            requestError?.message ||
            "Unable to load roles.",
        );
      } finally {
        if (isCurrentRequest) setRolesLoading(false);
      }
    };

    fetchRoles();

    return () => {
      isCurrentRequest = false;
    };
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setIsSubmitting(true);

    try {
      if (
        !Utils.checkName(form.firstName.trim()) ||
        !Utils.checkName(form.lastName.trim())
      ) {
        throw new Error("First name and last name can only contain letters.");
      }

      const Authorization = userService.getJwt();
      const companyID = userService.getUserData().Compid;
      const adminID = getStoredUserId();
      const phonePayload = getPhonePayload(phoneNumber);

      if (!companyID) {
        throw new Error("Company id was not found for this user.");
      }
      if (!adminID) {
        throw new Error("Admin id was not found for this user.");
      }
      if (!phoneNumber) {
        throw new Error("Phone number is required.");
      }
      if (rolesLoading) {
        throw new Error("Roles are still loading.");
      }
      if (!isUuid(form.roleID)) {
        throw new Error("Role id must be a valid UUID.");
      }

      const userRegister = {
        businessDescription: null,
        businessName: null,
        businessProfileCategoryId: null,
        businessProfileTypeId: null,
        countryOfResidence: "Lebanon",
        email: form.email.trim(),
        firstname: form.firstName.trim(),
        lastname: form.lastName.trim(),
        password: null,
        phoneNumber: phonePayload.phoneNumber,
        suffix: phonePayload.suffix,
        userType: "END_USER",
      };

      const response = await companyController.addCompanyEmployees({
        Authorization,
        addCompanyEmloyeeRequest: {
          adminID,
          birthdate: toApiBirthdate(form.birthdate),
          companyID,
          gender: {
            costumGenderName: form.gender === "CUSTOM" ? "Other" : null,
            genderName: form.gender,
            pronoun: getGenderPronoun(form.gender),
          },
          roleID: form.roleID,
          userID: null,
          userRegister,
        },
      });

      if (response?.isAxiosError || response?.response) {
        throw response;
      }

      setCreatedUserCreatedAt(new Date().toISOString());
      setShowSuccessModal(true);
    } catch (requestError) {
      setError(
        requestError?.response?.data?.message ||
          requestError?.message ||
          "Unable to create user.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <VendorLayout activeKey="users">
      <section className="vendor-add-user-page">
        <Link className="vendor-add-user-back" to={userManagementPath}>
          {translateLabel("txt_web_vendor_back_to_user_management", "Back to User Management")}
        </Link>

        <div className="vendor-add-user-heading">
          <div className="vendor-add-user-title">{translateLabel("txt_web_vendor_add_new_user", "Add New User")}</div>
          <div className="vendor-add-user-subtitle">
            {translateLabel("txt_web_vendor_create_company_user_description", "Create a new user account for your company")}
          </div>
        </div>

        <form
          className="vendor-add-user-form"
          onSubmit={handleSubmit}
          noValidate
        >
          <div className="vendor-add-user-card">
            <div className="vendor-add-user-card-heading">
              <div className="vendor-add-user-card-title"> {translateLabel("lnk_user_information", "User Information")} </div>
              <div className="vendor-add-user-card-subtitle">
                {translateLabel("txt_web_vendor_enter_new_user_details", "Enter the details for the new user account")}
              </div>
            </div>

            <div className="vendor-add-user-grid">
              <label className="vendor-add-user-field">
                <span> {translateLabel("input_first_name", "First Name")}<i>*</i>
                </span>
                <div>
                  <LuUserRound aria-hidden="true" />
                  <input
                    type="text"
                    value={form.firstName}
                    aria-label={translateLabel("input_first_name", "First Name")}
                    required
                    onChange={updateField("firstName")}
                  />
                </div>
              </label>

              <label className="vendor-add-user-field">
                <span> {translateLabel("input_last_name", "Last Name")}<i>*</i>
                </span>
                <div>
                  <LuUserRound aria-hidden="true" />
                  <input
                    type="text"
                    value={form.lastName}
                    aria-label={translateLabel("input_last_name", "Last Name")}
                    required
                    onChange={updateField("lastName")}
                  />
                </div>
              </label>

              <label className="vendor-add-user-field">
                <span> {translateLabel("input_email_address", "Email Address")}<i>*</i>
                </span>
                <div>
                  <LuMail aria-hidden="true" />
                  <input
                    type="email"
                    value={form.email}
                    aria-label={translateLabel("input_email_address", "Email Address")}
                    required
                    onChange={updateField("email")}
                  />
                </div>
              </label>

              <label className="vendor-add-user-field">
                <span> {translateLabel("input_phone_number", "Phone Number")}<i>*</i>
                </span>
                <div className="vendor-add-user-phone-input">
                  <PhoneInput
                    defaultCountry="LB"
                    international
                    countryCallingCodeEditable={false}
                    value={phoneNumber}
                    onChange={setPhoneNumber}
                    aria-label={translateLabel("input_phone_number", "Phone Number")}
                    required
                  />
                </div>
              </label>

              <label className="vendor-add-user-field">
                <span> {translateLabel("txt_web_vendor_birth_date", "Birth date")}<i>*</i>
                </span>
                <div>
                  <input
                    className="vendor-add-user-date-input"
                    type="date"
                    value={form.birthdate}
                    aria-label={translateLabel("txt_web_vendor_birth_date", "Birth date")}
                    required
                    onChange={updateField("birthdate")}
                  />
                </div>
              </label>

              <label className="vendor-add-user-field">
                <span> {translateLabel("input_gender", "Gender")}<i>*</i>
                </span>
                <div className="vendor-add-user-select">
                  <LuUsersRound aria-hidden="true" />
                  <select
                    value={form.gender}
                    aria-label={translateLabel("input_gender", "Gender")}
                    required
                    onChange={updateField("gender")}
                  >
                    <option value="" disabled> {translateLabel("txt_web_vendor_select_your_gender", "Select your gender")} </option>
                    <option value="FEMALE">{translateLabel("txt_end_user_female", "Female")}</option>
                    <option value="MALE">{translateLabel("txt_end_user_male", "Male")}</option>
                    <option value="CUSTOM">{translateLabel("txt_end_user_other", "Other")}</option>
                  </select>
                  <LuChevronDown aria-hidden="true" />
                </div>
              </label>

              <label className="vendor-add-user-field">
                <span> {translateLabel("txt_web_vendor_role", "Role")}<i>*</i>
                </span>
                <div className="vendor-add-user-select">
                  <LuUsersRound aria-hidden="true" />
                  <select
                    value={form.roleID}
                    aria-label={translateLabel("txt_web_vendor_role", "Role")}
                    required
                    disabled={rolesLoading}
                    onChange={updateField("roleID")}
                  >
                    <option value="" disabled>
                      {rolesLoading ? translateLabel("txt_web_vendor_loading_roles", "Loading roles...") : translateLabel("txt_web_vendor_select_role", "Select role")}
                    </option>
                    {roles.map((role) => (
                      <option value={role.roleID} key={role.roleID}>
                        {role.roleName}
                      </option>
                    ))}
                  </select>
                  <LuChevronDown aria-hidden="true" />
                </div>
              </label>
            </div>

            {error && <div className="vendor-add-user-error" role="alert">{translateAddUserFeedback(error)}</div>}
            {rolesError && (
              <div className="vendor-add-user-error" role="alert">{translateAddUserFeedback(rolesError)}</div>
            )}

            <div className="vendor-add-user-actions">
              <button
                className="vendor-add-user-create"
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? translateLabel("txt_web_vendor_creating_user", "Creating User...") : translateLabel("txt_web_vendor_create_user", "Create User")}
              </button>
              <Link
                className="vendor-add-user-cancel"
                to={userManagementPath}
              > {translateLabel("btn_cancel", "Cancel")} </Link>
            </div>
          </div>

          <aside className="vendor-add-user-note">
            <strong>{translateLabel("txt_web_vendor_new_user_invitation", "New User Invitation")}</strong>
            <div className="vendor-add-user-note-text">
              {translateLabel("txt_web_vendor_user_invitation_description", "The new user will receive an email invitation to set up their password and complete their profile. They will have access to the company data based on their assigned role.")}
            </div>
          </aside>
        </form>

        {showSuccessModal && (
          <div className="vendor-add-user-success-backdrop">
            <div
              className="vendor-add-user-success-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="vendor-add-user-success-title"
            >
              <div className="vendor-add-user-success-icon">
                <UserAddedSuccessIcon />
                <ActiveIcon />
              </div>
              <div
                className="vendor-add-user-success-title"
                id="vendor-add-user-success-title"
              >
                {translateLabel("txt_web_vendor_user_added_successfully", "User Added Successfully")}
              </div>
              <button
                type="button"
                onClick={() =>
                  navigate(userManagementPath, {
                    state: {
                      createdUser: {
                        createdAt:
                          createdUserCreatedAt || new Date().toISOString(),
                        email: form.email.trim(),
                        status: "INVITE_SENT",
                      },
                    },
                  })
                }
              > {translateLabel("btn_continue", "Continue")} </button>
            </div>
          </div>
        )}
      </section>
    </VendorLayout>
  );
}

export default VendorAddUser;
